import { useAuth } from '@/lib'
import { logger } from '@/ui'
import { AnalyticsBrowser } from '@customerio/cdp-analytics-browser'
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'

export type CustomerIoAnalytics = AnalyticsBrowser

const CustomerIoContext = createContext<CustomerIoAnalytics | null>(null)

interface CustomerIoProviderProps {
	children: ReactNode
}

export function CustomerIoProvider({ children }: CustomerIoProviderProps) {
	const [analytics, setAnalytics] = useState<AnalyticsBrowser | null>(null)
	const { user, isLoading } = useAuth()

	useEffect(() => {
		if (!import.meta.env.VITE_CUSTOMERIO_API_KEY || !import.meta.env.VITE_CUSTOMERIO_SITE_ID) {
			return
		}

		const cio = AnalyticsBrowser.load({
			cdnURL: 'https://cdp-eu.customer.io',
			writeKey: import.meta.env.VITE_CUSTOMERIO_API_KEY,
		}, {
			integrations: {
				'Customer.io In-App Plugin': {
					siteId: import.meta.env.VITE_CUSTOMERIO_SITE_ID,
				},
			},
		})

		logger.setCustomerIo(cio)
		setAnalytics(cio)
	}, [])

	useEffect(
		() => {
			if (!analytics) {
				return
			}

			if (isLoading) {
				return
			}

			if (!user) {
				analytics.reset()
			} else {
				analytics.identify(user.email)
			}
		},
		[
			analytics,
			isLoading,
			user,
			user?.email,
		],
	)

	return (
		<CustomerIoContext.Provider value={analytics}>
			{children}
		</CustomerIoContext.Provider>
	)
}

export function useCustomerIo(): CustomerIoAnalytics | null {
	return useContext(CustomerIoContext)
}
